import { FormattedMessage } from 'react-intl';

import { Receipt } from '@/components/ui/receipt';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { POOL } from '@/types/channels';
import { getChannelName } from '@/utils/channel-names';

interface BudgetLineItemProps {
  channelSlug: string;
  amount: number | null;
}

function BudgetLineItem({ channelSlug, amount }: BudgetLineItemProps) {
  const { formatCurrency } = useCurrencySymbol();

  return (
    <div className="flex justify-between gap-4 px-6">
      <Typography variant="body2" className="!text-fx-neutral-600">
        {getChannelName(channelSlug)}
      </Typography>
      <Typography variant="body2" className="!text-fx-neutral-600">
        {amount !== null ? formatCurrency(amount) : '-'}
      </Typography>
    </div>
  );
}

export function TotalBudgetReceipt() {
  const { formatCurrency } = useCurrencySymbol();
  const {
    hookForm: { getValues },
    totalBudget,
  } = useJobPostingContext();
  const channels = getValues('channels');
  const pool = getValues('pool');

  return (
    <Receipt>
      <Typography variant="h6" className="px-6">
        <FormattedMessage
          defaultMessage="Total Spend"
          description="Total spend header"
        />
      </Typography>

      <div className="bg-fx-neutral-200 h-px w-full" />

      {Object.entries(channels).map(([channelSlug, channel]) => (
        <BudgetLineItem
          key={channelSlug}
          channelSlug={channelSlug}
          amount={channel.totalChannelBudget}
        />
      ))}

      {pool && <BudgetLineItem key={POOL} channelSlug={POOL} amount={0} />}

      <div className="bg-fx-neutral-200 mx-auto mt-auto h-px w-[85%]" />

      <div className="flex items-center justify-between gap-4 px-6 pb-1">
        <Typography variant="caption" className="!text-fx-neutral-600">
          <FormattedMessage
            defaultMessage="Total"
            description="Total budget label"
          />
        </Typography>
        <Typography variant="h2">
          {totalBudget ? formatCurrency(totalBudget) : '-'}
        </Typography>
      </div>
    </Receipt>
  );
}
